import type { PublicacionPublic } from '@app/shared';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { qk } from '../../../lib/queryKeys';
import { publicacionesApi } from '../api/publicaciones.api';

import { ESTADO_CLASE, fechaLinda } from './estados';

// El calendario del Publicador: la vista "📆 Calendario" de Notion (por Fecha de
// publicación), armada en la app con los mismos datos, por día, semana o mes.
// Cada publicación lleva el color del estado; clic abre la página en Notion.

type Vista = 'dia' | 'semana' | 'mes';

function iso(d: Date): string {
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function sumarDias(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}
/** El lunes de la semana de `d`. */
function lunesDe(d: Date): Date {
  return sumarDias(new Date(d.getFullYear(), d.getMonth(), d.getDate()), -((d.getDay() + 6) % 7));
}

/** El rango que se pide a Notion según la vista y la fecha de referencia. */
function rango(vista: Vista, ref: Date): { desde: Date; hasta: Date } {
  if (vista === 'dia') return { desde: ref, hasta: ref };
  if (vista === 'semana') {
    const l = lunesDe(ref);
    return { desde: l, hasta: sumarDias(l, 6) };
  }
  return {
    desde: new Date(ref.getFullYear(), ref.getMonth(), 1),
    hasta: new Date(ref.getFullYear(), ref.getMonth() + 1, 0),
  };
}

export function Calendario({ className }: { className?: string }): JSX.Element {
  const es = useI18n();
  const t = es.publicar.calendario;
  const dias = t.dias.split('|');
  const meses = t.meses.split('|');
  const estados = es.publicar.lista.estados as Record<string, string>;
  const hoy = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
  const [vista, setVista] = useState<Vista>('mes');
  const [ref, setRef] = useState(hoy);

  const { desde, hasta } = rango(vista, ref);
  const desdeIso = iso(desde);
  const hastaIso = iso(hasta);
  const { data, isLoading, error } = useQuery({
    queryKey: qk.publicaciones.mes(desdeIso, hastaIso),
    queryFn: () => publicacionesApi.listar({ desde: desdeIso, hasta: hastaIso, limite: 100 }),
  });

  const porDia = new Map<string, PublicacionPublic[]>();
  for (const p of data ?? []) {
    if (!p.fecha) continue;
    porDia.set(p.fecha, [...(porDia.get(p.fecha) ?? []), p]);
  }
  for (const lista of porDia.values()) lista.sort((a, b) => (a.hora ?? '').localeCompare(b.hora ?? ''));
  const hoyIso = iso(hoy);

  const mover = (n: number): void => {
    if (vista === 'dia') setRef(sumarDias(ref, n));
    else if (vista === 'semana') setRef(sumarDias(ref, 7 * n));
    else setRef(new Date(ref.getFullYear(), ref.getMonth() + n, 1));
  };
  const titulo =
    vista === 'dia'
      ? `${dias[(ref.getDay() + 6) % 7] ?? ''} ${fechaLinda(iso(ref))}`
      : vista === 'semana'
        ? t.semanaDel
            .replace('{desde}', fechaLinda(desdeIso).slice(0, 5))
            .replace('{hasta}', fechaLinda(hastaIso).slice(0, 5))
        : `${(meses[ref.getMonth()] ?? '').replace(/^./, (c) => c.toUpperCase())} ${String(ref.getFullYear())}`;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => mover(-1)} aria-label={t.mesAnterior}>
          <ChevronLeft size={16} />
        </Button>
        <span className="min-w-[200px] text-center font-titulos text-base font-semibold text-text-strong">
          {titulo}
        </span>
        <Button variant="outline" size="sm" onClick={() => mover(1)} aria-label={t.mesSiguiente}>
          <ChevronRight size={16} />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setRef(hoy)}>
          {t.hoy}
        </Button>
        <div className="ml-auto flex rounded-md border border-border-subtle p-0.5" role="group">
          {(['dia', 'semana', 'mes'] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setVista(v)}
              aria-pressed={vista === v}
              className={cn(
                'rounded px-3 py-1 text-sm',
                vista === v ? 'bg-marca-primario text-white' : 'text-text-subtle hover:text-text-strong',
              )}
            >
              {t.vistas[v]}
            </button>
          ))}
        </div>
        {isLoading && <span className="text-xs text-text-subtle">{es.app.cargando}</span>}
      </div>
      {error && <Alert variant="error">{getErrorMessage(error, es.errores.generico.algoSalioMal)}</Alert>}

      {vista === 'mes' && (
        <Mes
          ref_={ref}
          porDia={porDia}
          hoyIso={hoyIso}
          dias={dias}
          estados={estados}
          mas={t.mas}
          vacio={data?.length === 0 ? t.vacio : null}
        />
      )}
      {vista === 'semana' && (
        <Semana desde={desde} porDia={porDia} hoyIso={hoyIso} dias={dias} estados={estados} />
      )}
      {vista === 'dia' && <Dia items={porDia.get(iso(ref)) ?? []} estados={estados} vacio={t.sinDia} />}
    </div>
  );
}

function Chip({
  p,
  estados,
  conHora,
}: {
  p: PublicacionPublic;
  estados: Record<string, string>;
  conHora?: boolean;
}): JSX.Element {
  // ciclo-contenido-sin-notion: ya no es un link (la página de Notion no existe más).
  return (
    <span
      title={`${p.hora ?? ''} ${p.titulo} · ${estados[p.estado] ?? p.estado}`}
      className={cn(
        'truncate rounded px-1 text-[11px] leading-4',
        ESTADO_CLASE[p.estado] ?? ESTADO_CLASE.Borrador,
      )}
    >
      {conHora && p.hora && <span className="mr-1 font-mono opacity-70">{p.hora}</span>}
      {p.titulo}
    </span>
  );
}

function Mes({
  ref_,
  porDia,
  hoyIso,
  dias,
  estados,
  mas,
  vacio,
}: {
  ref_: Date;
  porDia: Map<string, PublicacionPublic[]>;
  hoyIso: string;
  dias: string[];
  estados: Record<string, string>;
  mas: string;
  vacio: string | null;
}): JSX.Element {
  const primero = new Date(ref_.getFullYear(), ref_.getMonth(), 1);
  const ultimo = new Date(ref_.getFullYear(), ref_.getMonth() + 1, 0);
  const celdas: (Date | null)[] = [...Array<null>((primero.getDay() + 6) % 7).fill(null)];
  for (let d = 1; d <= ultimo.getDate(); d++) celdas.push(new Date(ref_.getFullYear(), ref_.getMonth(), d));
  while (celdas.length % 7) celdas.push(null);
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border-subtle bg-border-subtle">
      <div className="grid shrink-0 grid-cols-7 gap-px">
        {dias.map((d) => (
          <div
            key={d}
            className="bg-surface-muted px-2 py-1 text-center text-[11px] font-semibold uppercase tracking-wide text-text-subtle"
          >
            {d}
          </div>
        ))}
      </div>
      {vacio && <div className="bg-surface px-3 py-1 text-xs text-text-subtle">{vacio}</div>}
      <div className="grid min-h-0 flex-1 auto-rows-fr grid-cols-7 gap-px border-t border-border-subtle">
        {celdas.map((dia, i) => {
          const clave = dia ? iso(dia) : '';
          const items = dia ? (porDia.get(clave) ?? []) : [];
          const visibles = items.slice(0, 3);
          return (
            <div
              key={i}
              className={cn(
                'flex min-h-0 flex-col gap-0.5 bg-surface p-1',
                !dia && 'bg-surface-alt',
                clave === hoyIso && 'bg-surface-primario',
              )}
            >
              {dia && (
                <span
                  className={cn(
                    'text-[11px] leading-4 text-text-faint',
                    clave === hoyIso && 'font-bold text-marca-primario-oscuro',
                  )}
                >
                  {dia.getDate()}
                </span>
              )}
              {visibles.map((p) => (
                <Chip key={p.id} p={p} estados={estados} />
              ))}
              {items.length > visibles.length && (
                <span className="px-1 text-[10px] text-text-faint">
                  {mas.replace('{n}', String(items.length - visibles.length))}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Semana({
  desde,
  porDia,
  hoyIso,
  dias,
  estados,
}: {
  desde: Date;
  porDia: Map<string, PublicacionPublic[]>;
  hoyIso: string;
  dias: string[];
  estados: Record<string, string>;
}): JSX.Element {
  const columnas = Array.from({ length: 7 }, (_, i) => sumarDias(desde, i));
  return (
    <div className="grid min-h-0 flex-1 grid-cols-7 gap-px overflow-hidden rounded-lg border border-border-subtle bg-border-subtle">
      {columnas.map((d, i) => {
        const clave = iso(d);
        const items = porDia.get(clave) ?? [];
        return (
          <div
            key={clave}
            className={cn('flex min-h-0 flex-col bg-surface', clave === hoyIso && 'bg-surface-primario')}
          >
            <div
              className={cn(
                'shrink-0 border-b border-border-subtle bg-surface-muted px-2 py-1 text-center text-[11px] font-semibold uppercase tracking-wide text-text-subtle',
                clave === hoyIso && 'text-marca-primario-oscuro',
              )}
            >
              {dias[i]} {d.getDate()}
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-1.5">
              {items.map((p) => (
                <Chip key={p.id} p={p} estados={estados} conHora />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Dia({
  items,
  estados,
  vacio,
}: {
  items: PublicacionPublic[];
  estados: Record<string, string>;
  vacio: string;
}): JSX.Element {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto rounded-lg border border-border-subtle bg-surface shadow-card">
      {items.length === 0 && <p className="p-4 text-sm text-text-subtle">{vacio}</p>}
      <ul className="divide-y divide-border-subtle">
        {items.map((p) => (
          <li key={p.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
            <span className="w-12 shrink-0 font-mono text-xs text-text-subtle">{p.hora ?? '—'}</span>
            <span
              className={cn(
                'w-24 shrink-0 rounded-full px-2 py-0.5 text-center text-xs font-medium',
                ESTADO_CLASE[p.estado] ?? ESTADO_CLASE.Borrador,
              )}
            >
              {estados[p.estado] ?? p.estado}
            </span>
            <span className="min-w-0 flex-1 truncate font-medium text-text-strong">{p.titulo}</span>
            <span className="hidden shrink-0 text-xs text-text-subtle md:inline">
              {p.formato ?? ''} · {p.redes.join(', ')}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
